/**
 * CPD Record — auto-issued certificates + verified hours log.
 *
 * Re-uses Pillow-rendered PNG certs from /api/cpd/certificate/{id}.png.
 * Certs auto-refresh on every page load via /api/cpd/me.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import {
  Award, ArrowLeft, Download, Sparkles, Clock, BookOpen, Trophy, FileVideo,
  ShieldCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { trackFunnel } from "../utils/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  mastery:       { label: "Mastery",       icon: BookOpen, color: "text-violet-300 bg-violet-500/10 border-violet-500/30" },
  mock_part3:    { label: "Mock Part-3",   icon: Trophy,   color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
  lesson_review: { label: "Practice Review", icon: FileVideo, color: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
};

export default function CpdRecord() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/cpd/me`, { withCredentials: true });
      setData(d);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Funnel: page viewed
  useEffect(() => { trackFunnel("cpd", "viewed"); }, []);

  const downloadCert = (id) => {
    trackFunnel("cpd", "cert_downloaded", { cert_id: id });
    const url = `${API}/api/cpd/certificate/${id}.png`;
    // Fire as a fetch with credentials, then download blob (cookies needed).
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `ndg-cpd-${id}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Couldn't download certificate"));
  };

  if (loading) {
    return <div className="min-h-dvh no-overflow-x bg-[#050505] grid place-items-center text-zinc-500 text-sm">Loading…</div>;
  }

  const items = data?.items || [];

  return (
    <div className="min-h-dvh no-overflow-x bg-[#050505] text-white font-outfit pb-24" data-testid="cpd-page">
      <header className="sticky top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#050505]/85 border-b border-white/[0.07]">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="cpd-back" onClick={() => goBack()} className="p-2 -ml-2 rounded-lg hover:bg-white/5">
            <ArrowLeft className="w-4 h-4 text-zinc-400" />
          </button>
          <div className="flex-1">
            <p className="text-sm font-bold text-white">CPD Record</p>
            <p className="text-[10.5px] text-zinc-500 uppercase tracking-[0.18em]">Verified continuing professional development</p>
          </div>
          <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* SUMMARY */}
        <section data-testid="cpd-summary" className="rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#16120a] via-[#0c0c0c] to-[#0a0a0a] p-5 md:p-7">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-3 inline-flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> Auto-issued
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold leading-tight">
            <span data-testid="cpd-total-hours" className="text-gold-gradient">{data?.total_hours ?? 0} hours</span> on record.
          </h1>
          <p className="mt-3 text-[13.5px] text-zinc-300 leading-relaxed max-w-xl">
            Every Mastery skill mastered, Mock Part-3 graded A or B, and Lesson Review with three or more demonstrated competencies issues a certificate automatically. Show this to your registrar, franchise, or Standards Check examiner.
          </p>
          <div className="mt-5 grid grid-cols-3 gap-3">
            {Object.entries(KIND_META).map(([key, meta]) => {
              const Icon = meta.icon;
              return (
                <div key={key} className="rounded-xl border border-white/[0.07] bg-white/[0.025] p-3 text-center">
                  <Icon className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" />
                  <div className="font-display text-xl font-bold text-white">{data?.by_kind?.[key] ?? 0}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-[0.16em] mt-0.5">{meta.label}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CERT LIST */}
        {items.length === 0 ? (
          <section data-testid="cpd-empty" className="rounded-3xl border border-white/[0.07] bg-white/[0.025] p-8 text-center">
            <Award className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <div className="font-display text-lg font-semibold mb-1">No certificates yet</div>
            <p className="text-[13px] text-zinc-400 max-w-md mx-auto">
              Master a skill, complete a Mock Part-3, or upload a Lesson Review with at least 3 demonstrated competencies — your first certificate appears the moment you do.
            </p>
            <div className="mt-5 flex flex-wrap gap-2 justify-center">
              <Button data-testid="cpd-empty-mastery" onClick={() => nav("/instructor/mastery")} variant="outline" className="h-9 px-3 bg-transparent border border-white/15 text-white text-[12px] font-semibold rounded-md">
                Open Mastery
              </Button>
              <Button data-testid="cpd-empty-review" onClick={() => nav("/lesson-review")} className="h-9 px-3 bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] text-[12px] font-bold rounded-md">
                Practice Review
              </Button>
            </div>
          </section>
        ) : (
          <section>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-3 flex items-center gap-2">
              <Award className="w-3 h-3" /> Certificates · {items.length}
            </div>
            <div className="space-y-2.5">
              {items.map((c) => {
                const meta = KIND_META[c.kind] || { label: c.kind, icon: Award, color: "" };
                const Icon = meta.icon;
                return (
                  <div key={c.id} data-testid={`cpd-item-${c.id}`} className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13.5px] font-semibold text-white truncate">{c.title}</div>
                      <div className="text-[11.5px] text-zinc-400 truncate">{c.subtitle}</div>
                      <div className="text-[10.5px] text-zinc-500 mt-1 flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        {c.minutes} min
                        <span className="text-zinc-700">·</span>
                        {(c.earned_at || "").slice(0, 10)}
                      </div>
                    </div>
                    <Button
                      data-testid={`cpd-download-${c.id}`}
                      onClick={() => downloadCert(c.id)}
                      variant="outline"
                      className="h-9 px-3 bg-transparent border border-[#D4AF37]/35 hover:bg-[#D4AF37]/[0.06] text-[#F3E5AB] text-[12px] font-semibold rounded-md flex-shrink-0"
                    >
                      <Download className="w-3.5 h-3.5 mr-1.5" />
                      PNG
                    </Button>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
